class DoctorSearch {
  constructor() {
    this.googleSpreedSheet = JSON.parse(
      document.querySelector("#google-spreed-sheet").textContent
    ).url;

    this.getSpreedSheetContent();
  }

  async getSpreedSheetContent() {
    try {
      const request = await fetch(this.googleSpreedSheet);
      const response = await request.text();
      const doctorsData = Papa.parse(response, {
        header: true,
      });
      const formattedDoctorsData = this.formatDoctorObject(doctorsData.data);
      this.dispatchSpreedSheetData(formattedDoctorsData);
    } catch (error) {
      console.error(`DoctorSearch | getSpreedSheetContent | Error: ${error}`);
    }
  }

  dispatchSpreedSheetData(doctorsData) {
    const event = new CustomEvent("doctors-data-parsed", {
      detail: doctorsData,
    });
    document.dispatchEvent(event);
  }

  formatDoctorObject(data) {
    const tagsKey = data.length > 0
      ? Object.keys(data[0]).find((key) => key.startsWith("Tags"))
      : null;

    return data.map((doctor) => {
      return {
        name: doctor["Name"],
        webAddress: doctor["Web address"] ? doctor["Web address"] : "",
        specialty: doctor["Specialty"],
        location: doctor["Location"],
        fullLocation: doctor["Full location"],
        contactInfo: doctor["Contact info"],
        endorsement: doctor["Uqora community endorsement"],
        tags: (tagsKey ? doctor[tagsKey] : "") || "",
        state: doctor["State"],
      };
    });
  }
}

class DoctorSearchFilters extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    this.specialtyFilters = document.querySelector(
      "doctor-search-specialty-filters"
    );

    this.locationFilters = document.querySelector(
      "doctor-search-location-filter"
    );

    document.addEventListener("filter-specialties", (event) => {
      const specialties = event.detail;
      this.filterDoctorsBySpecialties(specialties);
    });

    document.addEventListener("filter-locations", (event) => {
      const location = event.detail;
      this.filterDoctorsByLocations(location);
    });
  }

  filterDoctorsBySpecialties(specialties) {
    this.dispatchFilterEvent({
      specialties,
      location: this.locationFilters.getLocationValue(),
    });
  }

  filterDoctorsByLocations(location) {
    this.dispatchFilterEvent({
      specialties: this.specialtyFilters.getCheckedValues(),
      location,
    });
  }

  dispatchFilterEvent(values) {
    const event = new CustomEvent("doctor-search-filter", {
      detail: values,
    });
    document.dispatchEvent(event);
  }

  disconnectedCallback() {
    document.removeEventListener("filter-specialties");
    document.removeEventListener("filter-locations");
  }
}

class SpecialtyFilters extends HTMLElement {
  constructor() {
    super();
    this.doctorsData = [];
    this.specialties = [];
    this.selectedSpecialties = [];
  }

  connectedCallback() {
    this.combobox = this.querySelector("#specialty--combobox");
    this.inputArea = this.querySelector("#specialty--input-area");
    this.input = this.querySelector("#specialty--input");
    this.dropdown = this.querySelector("#specialty--listbox");
    this.loadingSpinner = this.querySelector("#specialty--loading");

    this._onDoctorsDataParsed = (event) => {
      this.doctorsData = event.detail;
      this.getSpecialties();
      this.renderDropdownOptions(this.specialties);
      this.removeLoadingState();
    };
    this._onDocumentClick = this.onDocumentClick.bind(this);

    document.addEventListener("doctors-data-parsed", this._onDoctorsDataParsed);
    document.addEventListener("click", this._onDocumentClick);

    this.input.addEventListener("input", this.onInputChange.bind(this));
    this.input.addEventListener("focus", this.openDropdown.bind(this));
    this.input.addEventListener("keydown", this.onKeydown.bind(this));
    this.inputArea.addEventListener("click", this.onInputAreaClick.bind(this));
    this.dropdown.addEventListener("click", this.onOptionClick.bind(this));
  }

  getSpecialties() {
    let tags = this.doctorsData.map((doctor) => doctor.tags.split(",")).flat();
    tags = tags.map((tag) => tag.trim()).filter(Boolean);
    const uniqueTags = new Set(tags);
    this.specialties = Array.from(uniqueTags).sort();
  }

  renderDropdownOptions(specialties) {
    if (specialties.length === 0) {
      this.dropdown.innerHTML = `<li class="combobox__option combobox__option--empty">No results</li>`;
      return;
    }
    this.dropdown.innerHTML = specialties
      .map((specialty) => {
        const isSelected = this.selectedSpecialties.includes(specialty);
        return `
          <li
            class="combobox__option${isSelected ? " combobox__option--selected" : ""}"
            role="option"
            aria-selected="${isSelected}"
            data-value="${specialty}"
          >${specialty}</li>
        `;
      })
      .join("");
  }

  onInputChange() {
    const query = this.input.value.trim().toLowerCase();
    const filtered = this.specialties.filter((s) =>
      s.toLowerCase().includes(query)
    );
    this.renderDropdownOptions(filtered);
    this.openDropdown();
  }

  onInputAreaClick(event) {
    if (event.target.closest(".combobox__pill-remove")) return;
    this.input.focus();
    this.openDropdown();
  }

  onOptionClick(event) {
    const option = event.target.closest(".combobox__option");
    if (!option || option.classList.contains("combobox__option--empty")) return;
    const value = option.dataset.value;
    this.toggleSpecialty(value);
    this.input.value = "";
    this.renderDropdownOptions(this.specialties);
    this.input.focus();
    this.dispatchFilterEvent(this.selectedSpecialties);
  }

  toggleSpecialty(value) {
    const index = this.selectedSpecialties.indexOf(value);
    if (index === -1) {
      this.selectedSpecialties.push(value);
    } else {
      this.selectedSpecialties.splice(index, 1);
    }
    this.renderPills();
  }

  renderPills() {
    this.inputArea.querySelectorAll(".combobox__pill").forEach((p) => p.remove());
    this.selectedSpecialties.forEach((specialty) => {
      const pill = document.createElement("span");
      pill.className = "combobox__pill";
      pill.dataset.value = specialty;
      pill.innerHTML = `${specialty}<button class="combobox__pill-remove" aria-label="Remove ${specialty}" data-value="${specialty}">×</button>`;
      pill.querySelector(".combobox__pill-remove").addEventListener("click", (event) => {
        event.stopPropagation();
        this.removeSpecialty(event.currentTarget.dataset.value);
      });
      this.inputArea.insertBefore(pill, this.input);
    });
  }

  removeSpecialty(value) {
    this.selectedSpecialties = this.selectedSpecialties.filter((s) => s !== value);
    this.renderPills();
    this.renderDropdownOptions(this.specialties);
    this.dispatchFilterEvent(this.selectedSpecialties);
  }

  onKeydown(event) {
    if (event.key === "Escape") {
      this.closeDropdown();
    } else if (
      event.key === "Backspace" &&
      this.input.value === "" &&
      this.selectedSpecialties.length > 0
    ) {
      const last = this.selectedSpecialties[this.selectedSpecialties.length - 1];
      this.removeSpecialty(last);
    }
  }

  openDropdown() {
    this.combobox.classList.add("combobox--open");
    this.combobox.setAttribute("aria-expanded", "true");
  }

  closeDropdown() {
    this.combobox.classList.remove("combobox--open");
    this.combobox.setAttribute("aria-expanded", "false");
  }

  onDocumentClick(event) {
    if (!this.contains(event.target)) {
      this.closeDropdown();
    }
  }

  dispatchFilterEvent(values) {
    const event = new CustomEvent("filter-specialties", {
      detail: values,
    });
    document.dispatchEvent(event);
  }

  getCheckedValues() {
    return this.selectedSpecialties;
  }

  removeLoadingState() {
    this.loadingSpinner.dataset.showLoading = false;
  }

  disconnectedCallback() {
    document.removeEventListener("doctors-data-parsed", this._onDoctorsDataParsed);
    document.removeEventListener("click", this._onDocumentClick);
  }
}

class LocationFilter extends HTMLElement {
  constructor() {
    super();
    this.doctorsData = [];
    this.locationsArray = [];
    this.selectedLocation = "All";
  }

  connectedCallback() {
    this.combobox = this.querySelector("#location--combobox");
    this.inputArea = this.querySelector("#location--input-area");
    this.input = this.querySelector("#location--input");
    this.dropdown = this.querySelector("#location--listbox");
    this.loadingSpinner = this.querySelector("#location--loading");

    this._onDoctorsDataParsed = (event) => {
      this.doctorsData = event.detail;
      this.getLocations();
      this.renderDropdownOptions(this.locationsArray);
      this.removeLoadingState();
    };
    this._onDocumentClick = this.onDocumentClick.bind(this);

    document.addEventListener("doctors-data-parsed", this._onDoctorsDataParsed);
    document.addEventListener("click", this._onDocumentClick);

    this.input.addEventListener("input", this.onInputChange.bind(this));
    this.input.addEventListener("focus", this.openDropdown.bind(this));
    this.input.addEventListener("keydown", this.onKeydown.bind(this));
    this.inputArea.addEventListener("click", () => {
      this.input.focus();
      this.openDropdown();
    });
    this.dropdown.addEventListener("click", this.onOptionClick.bind(this));
  }

  getLocations() {
    const states = new Set(
      this.doctorsData.map((doctor) => doctor.state).filter(Boolean)
    );
    this.locationsArray = ["All", ...Array.from(states).sort()];
  }

  renderDropdownOptions(locations) {
    if (locations.length === 0) {
      this.dropdown.innerHTML = `<li class="combobox__option combobox__option--empty">No results</li>`;
      return;
    }
    this.dropdown.innerHTML = locations
      .map((location) => {
        const isSelected = location === this.selectedLocation;
        return `
          <li
            class="combobox__option${isSelected ? " combobox__option--selected" : ""}"
            role="option"
            aria-selected="${isSelected}"
            data-value="${location}"
          >${location}</li>
        `;
      })
      .join("");
  }

  onInputChange() {
    const query = this.input.value.trim().toLowerCase();
    const filtered = this.locationsArray.filter((l) =>
      l.toLowerCase().includes(query)
    );
    this.renderDropdownOptions(filtered);
    this.openDropdown();
  }

  onOptionClick(event) {
    const option = event.target.closest(".combobox__option");
    if (!option || option.classList.contains("combobox__option--empty")) return;
    const value = option.dataset.value;
    this.selectedLocation = value;
    this.input.value = value === "All" ? "" : value;
    this.renderDropdownOptions(this.locationsArray);
    this.closeDropdown();
    this.dispatchFilterEvent(value);
  }

  onKeydown(event) {
    if (event.key === "Escape") {
      this.closeDropdown();
      this.restoreInputDisplay();
    }
  }

  restoreInputDisplay() {
    this.input.value = this.selectedLocation === "All" ? "" : this.selectedLocation;
  }

  openDropdown() {
    this.combobox.classList.add("combobox--open");
    this.combobox.setAttribute("aria-expanded", "true");
  }

  closeDropdown() {
    this.combobox.classList.remove("combobox--open");
    this.combobox.setAttribute("aria-expanded", "false");
    this.restoreInputDisplay();
  }

  onDocumentClick(event) {
    if (!this.contains(event.target)) {
      this.closeDropdown();
    }
  }

  dispatchFilterEvent(location) {
    const event = new CustomEvent("filter-locations", {
      detail: location,
    });
    document.dispatchEvent(event);
  }

  removeLoadingState() {
    this.loadingSpinner.dataset.showLoading = false;
  }

  getLocationValue() {
    return this.selectedLocation;
  }

  disconnectedCallback() {
    document.removeEventListener("doctors-data-parsed", this._onDoctorsDataParsed);
    document.removeEventListener("click", this._onDocumentClick);
  }
}

class SearchResults extends HTMLElement {
  constructor() {
    super();
    this.cardsPerPage = 10;

    this.doctorsData = [];
    this.domParser = new DOMParser();
  }

  connectedCallback() {
    this.resultsList = this.querySelector("#results--list");
    this.paginationContainer = this.querySelector("#results--pagination");
    this.loadingSpinner = this.querySelector("#results--loading");
    this.emptyState = this.querySelector("#results--empty");

    document.addEventListener("doctors-data-parsed", (event) => {
      this.doctorsData = event.detail;
      this.renderDoctors();
      this.removeLoadingState();
    });

    document.addEventListener("doctor-search-filter", (event) => {
      const activeFilters = event.detail;
      this.filterDoctors(activeFilters);
    });

    this.paginationContainer.addEventListener(
      "click",
      this.paginationHandler.bind(this)
    );
  }

  filterDoctors(activeFilters) {
    const { specialties, location } = activeFilters;

    const filteredBySpecialties = this.filterDoctorsBySpecialties(specialties);
    const filteredByLocation = this.filterDoctorsByLocation(
      location,
      filteredBySpecialties
    );

    this.renderDoctors(filteredByLocation);
    this.scrollSectionIntoView();
  }

  filterDoctorsBySpecialties(specialties) {
    if (specialties.length === 0) return this.doctorsData;

    return this.doctorsData.filter((doctor) => {
      let doctorTags = doctor.tags.split(",").map((tag) => tag.trim());
      return doctorTags.some((tag) => specialties.includes(tag));
    });
  }

  filterDoctorsByLocation(location, filteredDocsArray) {
    if (location === "All") return filteredDocsArray;
    return filteredDocsArray.filter((doctor) => doctor.state === location);
  }

  renderDoctors(doctorsData = this.doctorsData) {
    this.resultsList.innerHTML = "";

    this.toggleEmptyResultsMessage(doctorsData.length === 0);
    this.togglePagination(doctorsData.length === 0);
    if (doctorsData.length === 0) return;

    const pages = Math.ceil(doctorsData.length / this.cardsPerPage);
    this.buildPaginationButtons(pages);
    const dataPaginated = this.buildPaginationPages(
      doctorsData,
      this.cardsPerPage
    );

    this.resultsList.innerHTML = `
      ${dataPaginated
        .map((doctorsData, index) => {
          return `
            <div
              class="doctor-results-page"
              data-doctor-results-page="${index + 1}"
              data-doctor-results-active-page="${index === 0 ? true : false}"
            >
              ${doctorsData
                .map((doctor) => {
                  return `
                    <div class="doctor-card">
                      <h3>
                        <a
                          href="${doctor.webAddress}"
                          title="${doctor.name}'s web address"
                          target="_blank"
                          >${doctor.name}</a
                        >
                      </h3>
                      <p>${doctor.specialty}</p>
                      <details>
                        <summary>
                          ${doctor.location} <span>(see address)</span>
                        </summary>
                        <p>${doctor.fullLocation}</p>
                      </details>
                      <p>Phone number: ${doctor.contactInfo}</p>
                      
                     ${this.parseEndorsementString(doctor.endorsement)}
                      
                      <ul class="doctor-tags">
                        ${doctor.tags
                          .split(",")
                          .map((tag) => `<li><span>${tag.trim()}</span></li>`)
                          .join("")}
                      </ul>
                    </div>
                  `;
                })
                .join("")}
            </div>
          `;
        })
        .join("")}
    `;
  }

  paginationHandler(event) {
    const newActiveBtn = event.target.closest("button");
    if (!newActiveBtn) return;
    const { doctorResultsPage } = newActiveBtn.dataset;
    const newActivePage = this.resultsList.querySelector(
      `.doctor-results-page[data-doctor-results-page="${doctorResultsPage}"]`
    );

    const prevActiveBtn = this.paginationContainer.querySelector(
      `button[data-doctor-results-active-page="true"]`
    );
    const prevActivePage = this.resultsList.querySelector(
      `[data-doctor-results-active-page="true"]`
    );

    prevActiveBtn.dataset.doctorResultsActivePage = false;
    newActiveBtn.dataset.doctorResultsActivePage = true;
    prevActivePage.dataset.doctorResultsActivePage = false;
    newActivePage.dataset.doctorResultsActivePage = true;

    this.scrollSectionIntoView();
  }

  buildPaginationButtons(pages) {
    let paginationButtons = [];
    let page = 1;
    while (page <= pages) {
      let buttonMarkup = `
      <button 
        data-doctor-results-active-page="${page === 1 ? true : false}" 
        data-doctor-results-page="${page}"
        aria-label="Button for page ${page}">
          ${page}
      </button>`;
      paginationButtons.push(buttonMarkup);
      page++;
    }

    this.paginationContainer.innerHTML = ``;
    this.paginationContainer.innerHTML = `
      ${paginationButtons.map((el) => el).join("")}
    `;
  }

  buildPaginationPages(data, pages) {
    let paginatedArray = [];
    for (let index = 0; index < data.length; index += pages) {
      paginatedArray.push(data.slice(index, index + pages));
    }
    return paginatedArray;
  }

  parseEndorsementString(stringToParse) {
    const parsedHTML = this.domParser.parseFromString(
      stringToParse,
      "text/html"
    );
    const outerHtml = parsedHTML.querySelector("body").outerHTML;
    return `<div class="doctor-card--endorsement"><p>${outerHtml}</p></div>`;
  }

  removeLoadingState() {
    this.loadingSpinner.dataset.showLoading = false;
  }

  toggleEmptyResultsMessage(flag) {
    this.emptyState.dataset.showEmpty = flag;
  }

  togglePagination(flag) {
    this.paginationContainer.dataset.showPagination = !flag;
  }

  scrollSectionIntoView() {
    document.querySelector("#doctor-search--container").scrollIntoView({
      behavior: "smooth",
    });
  }

  disconnectedCallback() {
    document.removeEventListener("doctors-data-parsed");
    document.removeEventListener("doctor-search-filter");
    this.paginationContainer.addEventListener("click");
  }
}

customElements.define("doctor-search-specialty-filters", SpecialtyFilters);
customElements.define("doctor-search-location-filter", LocationFilter);
customElements.define("doctor-search-filters", DoctorSearchFilters);
customElements.define("doctor-search-results", SearchResults);

const doctorSearch = new DoctorSearch();
